/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

type DecisionMaker = StarterDashboardV2PageData['petition']['decisionMakersConnection']['nodes'][number];

type UseDmCardsReturn = ModelHookResult<
	{
		ask: string;
		slug: string;
		starterDisplayName: string;
		signatureCount: number;
		emailDm: { emailSubjectLine: string; emailBody: readonly string[] };
		decisionMakers: readonly DecisionMaker[];
		allDmResponses: ReadonlyArray<{ id: string; decisionMaker: { id: string } }>;
	},
	{
		trackProfileLinkClick: (decisionMakerId: string) => void;
		trackEmailDmClick: (decisionMakerId: string) => void;
		trackReadResponseClick: (responseId: string) => void;
	}
>;

export function useDmCards(): UseDmCardsReturn {
	const track = useTracking();

	const {
		data: {
			petition: {
				ask,
				slug,
				user: { displayName: starterDisplayName },
				signatureState: {
					signatureCount: { total: signatureCount },
				},
				decisionMakersConnection: { nodes: decisionMakers },
				emailDm,
				dmResponsesConnection: { nodes: allDmResponses },
			},
		},
	} = useStarterDashboardV2PageData();

	const trackProfileLinkClick = useCallback(
		(decisionMakerId: string) => {
			void track('decision_maker_profile_link_click', {
				decision_maker_id: decisionMakerId,
				source_location: 'starter_dashboard',
			});
		},
		[track],
	);

	const trackEmailDmClick = useCallback(
		(decisionMakerId: string) => {
			void track('starter_dashboard_dm_list_email_click', { decision_maker_id: decisionMakerId });
		},
		[track],
	);

	const trackReadResponseClick = useCallback(
		(responseId: string) => {
			void track('starter_dashboard_read_dm_response_click', { response_id: responseId });
		},
		[track],
	);

	return {
		data: {
			ask,
			slug,
			starterDisplayName,
			signatureCount,
			emailDm,
			decisionMakers,
			allDmResponses,
		},
		actions: {
			trackProfileLinkClick,
			trackEmailDmClick,
			trackReadResponseClick,
		},
	};
}
